import { AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import { COMMISSION_MEETINGS_URL } from 'SharedDir/backend/endpoints/commissions';
import {
  ApplicationMeetingBare,
  CommissionDecisionBare,
  CommissionDecisionFull,
  CommissionMeetingBare,
  CommissionMeetingCreate,
  CommissionMeetingFull,
  CommissionMeetingScheduleIn,
  CommissionMeetingUpdate,
  ParagraphCommissionDecisionBare,
} from 'SharedDir/declarations/commissions';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';

type BackendCommissionMeetingsPaginated = Paginated<CommissionMeetingBare>;

export default class CommissionMeetingsApiClass extends SimpleApi<
  CommissionMeetingFull,
  CommissionMeetingCreate,
  CommissionMeetingUpdate,
  CommissionMeetingBare
> {
  constructor() {
    super(COMMISSION_MEETINGS_URL);
  }

  private static normalizeMeetingFull(raw: any): CommissionMeetingFull {
    const applications = (raw?.applications ?? []) as any[];
    const meetingApplications: ApplicationMeetingBare[] = applications.map(
      (applicationMeeting) => ({
        id: applicationMeeting.id,
        applicationId: applicationMeeting.applicationId,
        application: applicationMeeting.application ?? null,
        createdAt: applicationMeeting.createdAt ?? null,
        createdBy: applicationMeeting.createdBy ?? null,
      })
    );
    const decision = raw?.decision
      ? ({
          id: raw.decision.id,
          meetingId: raw.decision.meetingId ?? null,
          status: raw.decision.status ?? null,
          number: raw.decision.number ?? null,
          date: raw.decision.date ?? null,
          fileId: raw.decision.fileId ?? null,
          file: raw.decision.file ?? null,
          createdAt: raw.decision.createdAt ?? null,
          updatedAt: raw.decision.updatedAt ?? null,
          paragraphs: ((raw.decision.paragraphs ?? []) as any[]).map(
            (paragraph): ParagraphCommissionDecisionBare => ({
              id: paragraph.id,
              decisionId: paragraph.decisionId ?? raw.decision.id,
              applicationId: paragraph.applicationId ?? null,
              applicationSubjectId: paragraph.applicationSubjectId ?? null,
              applicationSubject: paragraph.applicationSubject ?? null,
              paragraphText: paragraph.paragraphText ?? null,
              resolutionText: paragraph.resolutionText ?? null,
              resultVote: paragraph.resultVote ?? null,
              voteDate: paragraph.voteDate ?? null,
              votedBy: paragraph.votedBy ?? null,
              createdAt: paragraph.createdAt ?? null,
              updatedAt: paragraph.updatedAt ?? null,
            })
          ),
        } satisfies CommissionDecisionFull)
      : null;

    return {
      id: raw?.id,
      status: raw?.status,

      name: raw?.name ?? null,
      planCommissionDate: raw?.planCommissionDate ?? null,
      factCommissionDate: raw?.factCommissionDate ?? null,

      createdAt: raw?.createdAt ?? null,
      updatedAt: raw?.updatedAt ?? null,
      createdBy: raw?.createdBy ?? null,
      updatedBy: raw?.updatedBy ?? null,

      applications: meetingApplications,
      decision,

      createdUser: raw?.createdUser ?? null,
      updatedUser: raw?.updatedUser ?? null,
    };
  }

  public async get(id: string | number): Promise<CommissionMeetingFull> {
    const { data: raw } = (await http({
      url: `${this.url}/${id}`,
      method: 'get',
    })) as AxiosResponse<any>;

    return CommissionMeetingsApiClass.normalizeMeetingFull(raw);
  }

  public async schedule(
    id: number,
    data: CommissionMeetingScheduleIn
  ): Promise<CommissionMeetingFull> {
    const { data: raw } = (await http({
      url: `${this.url}/${id}/schedule`,
      method: 'post',
      data,
    })) as AxiosResponse<any>;

    return CommissionMeetingsApiClass.normalizeMeetingFull(raw);
  }

  public async listApplications(id: number): Promise<ApplicationMeetingBare[]> {
    const meeting = await this.get(id);
    return meeting.applications;
  }

  public async addApplication(id: number, applicationId: number): Promise<ApplicationMeetingBare> {
    const { data: raw } = (await http({
      url: `${this.url}/${id}/append-applications`,
      method: 'post',
      data: { applications: [{ applicationId }] },
    })) as AxiosResponse<any>;

    const meeting = CommissionMeetingsApiClass.normalizeMeetingFull(raw);
    const applicationMeeting = meeting.applications.find(
      (item) => item.applicationId === applicationId
    );
    if (!applicationMeeting) {
      throw new Error(
        `Application ${applicationId} was not found in meeting ${id} append-applications response`
      );
    }
    return applicationMeeting;
  }

  public async removeApplication(id: number, applicationId: number): Promise<void> {
    await http({
      url: `${this.url}/${id}/remove-applications`,
      method: 'post',
      data: { applications: [{ applicationId }] },
    });
  }
}
